import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  Space,
  Typography,
  Tag,
  Tooltip,
  Popconfirm,
  message,
  Empty,
  Spin,
} from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import { AgentPickerDrawer } from '@/components/AgentPickerDrawer';
import {
  getAgentsForSkill,
  linkAgentToSkill,
  unlinkAgentFromSkill,
  type AgentLinkRow,
} from '@/api/skills';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// LinkedAgentsPanel — surface on SkillDetailPage that shows currently linked
// agents and lets the user wire/unwire connections without leaving the page.
//
// "Link agent" opens the agent picker: the SERVER lists the practice's agents, a page at a time, minus those already
// linked (2 Nov 2026). It used to offer the first 100 of the practice's agents from a store, so an agent past them
// could not be linked from here. Picking an agent links it.
//
// Mutations call /api/AiAgentSkill/Link and /Unlink. Both are idempotent on
// the backend (link upserts; unlink no-ops on inactive rows), so this UI
// optimistically updates and reverts on error.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  skillId: number;
  /** Skill name shown in the confirm dialogs so the user sees what they're acting on. */
  skillName: string;
}

export const LinkedAgentsPanel = observer(function LinkedAgentsPanel({ skillId, skillName }: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [linked, setLinked] = useState<AgentLinkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setLinked(await getAgentsForSkill(skillId));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  const handleLink = async (agentId: number, name?: string) => {
    setSaving(true);
    try {
      await linkAgentToSkill(agentId, skillId);
      void message.success(`Linked ${name ?? `agent ${agentId}`}.`);
      await refresh();
    } catch (err) {
      void message.error('Link failed: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (agent: AgentLinkRow) => {
    try {
      await unlinkAgentFromSkill(agent.id, skillId);
      void message.success(`Unlinked from ${agent.displayName ?? `agent ${agent.id}`}.`);
      // Optimistic remove — refresh in the background to confirm.
      setLinked((prev) => prev.filter((a) => a.id !== agent.id));
      void refresh();
    } catch (err) {
      void message.error('Unlink failed: ' + (err as Error).message);
    }
  };

  return (
    <SectionCard
      title="Linked agents"
      subtitle={
        linked.length === 0
          ? 'No agents have linked this skill yet.'
          : `${linked.length} agent${linked.length === 1 ? '' : 's'} load this skill on demand.`
      }
      actions={
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
            Refresh
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            loading={saving}
            onClick={() => setAdding(true)}
          >
            Link agent
          </Button>
        </Space>
      }
    >
      {error && (
        <Text type="danger" style={{ display: 'block', marginBottom: 8 }}>
          {error}
        </Text>
      )}

      {loading && linked.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : linked.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No agents linked. Click Link agent to wire one."
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
          }}
        >
          {linked.map((a) => (
            <div
              key={a.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                background: isDark ? colors.slate[800] : '#FFFFFF',
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 8,
              }}
            >
              <RobotOutlined style={{ color: colors.violet[500], fontSize: 18 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link
                  to={`/agents/${a.id}`}
                  style={{
                    fontWeight: 600,
                    color: isDark ? colors.slate[50] : colors.slate[900],
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.displayName ?? `Agent ${a.id}`}
                </Link>
                {a.canonicalName && (
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {a.canonicalName}
                  </Text>
                )}
              </div>
              <Space size={4}>
                {a.patientApp && <Tag color="cyan">Patient</Tag>}
                {a.merchantApp && <Tag color="purple">Care team</Tag>}
              </Space>
              <Popconfirm
                title="Unlink this agent?"
                description={`Remove "${skillName}" from ${a.displayName ?? `agent ${a.id}`}'s skill set.`}
                okText="Unlink"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleUnlink(a)}
              >
                <Tooltip title="Unlink">
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </div>
          ))}
        </div>
      )}

      {/* Picking an agent links it; the skill then loads on demand (or always, when the agent's router is off). */}
      <AgentPickerDrawer
        open={adding}
        onClose={() => setAdding(false)}
        selectedId={null}
        allowClear={false}
        title={`Link an agent to “${skillName}”`}
        excludeIds={linked.map((l) => l.id)}
        onSelect={(id, agent) => {
          if (id != null) void handleLink(id, agent?.displayName);
        }}
      />
    </SectionCard>
  );
});
